/* ==========================================================================
   card-colors.js — the model's colours on a product card
   --------------------------------------------------------------------------
   Wherever a card carries the row of colour dots (snippets/card-colors.liquid:
   the collection page, search, the home page's rows of products), this file
   paints each dot with the colour of the product itself and, with a pointer,
   shows the photo of the colour under it in the card. Loaded on every page
   from layout/theme.liquid; a page without dots costs one observer.
   Without this file the dots are plain links showing a crop of each photo.
   ========================================================================== */
if (!window.CardColours) {
window.CardColours = class {
  /* Colour names in the shop's colour labels (English, from the product names) */
  static words = {
    hue: {
      red: 0, burgundy: 345, bordeaux: 345, cherry: 350, wine: 345, maroon: 345, coral: 10, rust: 18, peach: 20,
      orange: 28, apricot: 30, bronze: 30, mustard: 45, gold: 45, yellow: 52, lemon: 55, khaki: 60, olive: 75, moss: 75,
      lime: 85, pistachio: 90, cactus: 90, green: 115, emerald: 140, mint: 150, teal: 180, turquoise: 180, sky: 205,
      blue: 215, denim: 215, cobalt: 220, cornflower: 220, navy: 225, indigo: 235, lavender: 270, violet: 275,
      purple: 280, lilac: 285, plum: 300, fuchsia: 320, pink: 335, rose: 340
    },
    neutral: {
      black: 'black', onyx: 'black', jet: 'black',
      white: 'white', cream: 'white', ecru: 'white', ivory: 'white', optic: 'white', chalk: 'white', snow: 'white', milk: 'white', marble: 'white',
      grey: 'grey', gray: 'grey', silver: 'grey', melange: 'grey', charcoal: 'grey', anthracite: 'grey', slate: 'grey',
      beige: 'beige', sand: 'beige', camel: 'beige', taupe: 'beige', stone: 'beige', oatmeal: 'beige', oyster: 'beige', nude: 'beige', natural: 'beige', mushroom: 'beige', linen: 'beige', neutrals: 'beige',
      brown: 'brown', chestnut: 'brown', chocolate: 'brown', coffee: 'brown', cognac: 'brown', tan: 'brown', mocha: 'brown'
    },
    hls([r, g, b]) {
      r /= 255; g /= 255; b /= 255;
      const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
      if (max === min) return [0, l, 0];
      const d = max - min;
      const s = l <= 0.5 ? d / (max + min) : d / (2 - max - min);
      let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
      return [h * 60, l, s];
    },
    skin(c) { const [h, l, s] = this.hls(c); return h >= 5 && h <= 38 && l >= 0.42 && l <= 0.82 && s >= 0.18 && s <= 0.7; },
    fits(c, word) {
      const [h, l, s] = this.hls(c);
      if (word in this.hue) { const d = Math.abs(h - this.hue[word]) % 360; return s >= 0.25 && l > 0.12 && l < 0.92 && Math.min(d, 360 - d) <= 45; }
      switch (this.neutral[word]) {
        case 'black': return l < 0.2;
        case 'white': return l > 0.8 && s < 0.4;
        case 'grey': return s < 0.14 && l > 0.2 && l < 0.85;
        case 'beige': return h >= 18 && h <= 55 && s >= 0.08 && s <= 0.7 && l >= 0.45 && l <= 0.88;
        case 'brown': return h >= 8 && h <= 42 && l >= 0.12 && l <= 0.55;
        default: return true;
      }
    }
  };

  constructor() {
    this.watchColours();
    this.paintDots();
  }

  /* ---- colours of the model on a card ------------------------------------
     The dots (snippets/card-colors.liquid) are links to each colour. With a
     pointer, the dot under it shows that colour's photo in the card; the
     photo swaps in once loaded and goes when the pointer leaves the dots.
     Clicked while showing, the photo carries the page transition's picture
     name, so it travels into the product page (assets/wss.css, pdp-media). */
  watchColours() {
    document.addEventListener('pointerover', (e) => {
      if (e.pointerType === 'touch') return;
      const dot = e.target.closest('.card-colors__dot');
      if (dot) this.showColour(dot);
      const row = e.target.closest('.card-colors');
      if (row && !row.dataset.loaded) {
        row.dataset.loaded = '1';
        row.querySelectorAll('.card-colors__dot:not(.is-current)[data-card-img]').forEach((d) => { new Image().src = d.dataset.cardImg; });
      }
    });
    document.addEventListener('pointerout', (e) => {
      const row = e.target.closest('.card-colors');
      if (row && !row.contains(e.relatedTarget)) this.showColour(null, row);
    });
    document.addEventListener('focusin', (e) => { const dot = e.target.closest('.card-colors__dot'); if (dot) this.showColour(dot); });
    document.addEventListener('focusout', (e) => {
      const row = e.target.closest('.card-colors');
      if (row && !row.contains(e.relatedTarget)) this.showColour(null, row);
    });
    document.addEventListener('click', (e) => {
      const dot = e.target.closest('.card-colors__dot');
      if (!dot || e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const image = dot.closest('x-cell[data-product]')?.querySelector('.card-image.is-peek');
      const peek = image && image.querySelector('.card-peek');
      if (!peek || peek.getAttribute('src') !== dot.dataset.cardImg) return;
      document.querySelectorAll('.cards .card-image img').forEach((i) => { i.style.viewTransitionName = ''; });
      peek.style.viewTransitionName = 'pdp-media';
    });
  }

  /* ---- colours of the model: what colour a dot is -------------------------
     Each dot is painted with the colour of the product itself, read from a
     48px copy of that colour's photo (data-swatch, same origin as the shop,
     so the pixels are readable): the white ground — or a coloured one, learnt
     from the frame's edge — is left out, pixels nearer the middle count more,
     and the strongest patch of colour wins. When the colour's name (data-label)
     names a colour the strongest patch is not — a black sweater with green
     faces, a hat on a model — the strongest patch that is takes over. Results
     are kept in localStorage by photo address. If the photo cannot be read,
     the dot shows a crop of it (data-chip). */
  paintDots() {
    this.dotSeen = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      this.dotSeen.unobserve(entry.target);
      entry.target.querySelectorAll('.card-colors__dot').forEach((dot) => this.paintDot(dot));
    }), { rootMargin: '300px 0px' });
    const look = () => document.querySelectorAll('.card-colors:not([data-watched])').forEach((row) => {
      row.dataset.watched = '1';
      this.dotSeen.observe(row);
    });
    look();
    /* new cards arrive with every filter change, "show more" and the product page's recommendations */
    new MutationObserver(look).observe(document.body, { childList: true, subtree: true });
  }

  paintDot(dot) {
    const src = dot.dataset.swatch;
    if (!src || dot.dataset.painted) return;
    dot.dataset.painted = '1';
    const store = this.dotStore();
    if (store[src]) { dot.style.setProperty('--dot', `rgb(${store[src]})`); return; }
    const fallback = () => { if (dot.dataset.chip) dot.style.backgroundImage = `url("${dot.dataset.chip}")`; };
    const img = new Image();
    img.onload = () => {
      try {
        const w = img.naturalWidth, h = img.naturalHeight;
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        const rgb = this.dotColour(ctx.getImageData(0, 0, w, h).data, w, h, dot.dataset.label || '').join(',');
        dot.style.setProperty('--dot', `rgb(${rgb})`);
        store[src] = rgb;
        try {
          const keys = Object.keys(store);
          if (keys.length > 1500) keys.slice(0, 500).forEach((k) => delete store[k]);
          localStorage.setItem('sstories:dots:v1', JSON.stringify(store));
        } catch (e) { /* storage full or blocked: the colour is simply read again next time */ }
      } catch (e) { fallback(); }
    };
    img.onerror = fallback;
    /* 1–2 KB each: ahead of the page's photographs, or on the home page the dots wait behind them all */
    img.fetchPriority = 'high';
    img.src = src;
  }

  dotStore() {
    if (!this.dots) {
      try { this.dots = JSON.parse(localStorage.getItem('sstories:dots:v1') || '{}'); } catch (e) { this.dots = {}; }
    }
    return this.dots;
  }

  dotColour(data, w, h, label) {
    const px = (x, y) => { const i = (y * w + x) * 4; return [data[i], data[i + 1], data[i + 2]]; };

    /* The ground: the photos stand on white; a coloured one is the median of the frame's edge */
    const edge = [];
    for (let x = 0; x < w; x++) for (const y of [0, 1, h - 2, h - 1]) edge.push(px(x, y));
    for (let y = 0; y < h; y++) for (const x of [0, 1, w - 2, w - 1]) edge.push(px(x, y));
    edge.sort((a, b) => (a[0] + a[1] + a[2]) - (b[0] + b[1] + b[2]));
    const ground = edge[edge.length >> 1];
    const colouredGround = Math.min(...ground) < 236;

    /* Weighted 16-step colour bins; the counted pixels are kept for the name check */
    const bins = new Map();
    const kept = [];
    let total = 0;
    for (let y = 0; y < h; y++) {
      const wy = 1 - Math.abs((y + 0.5) / h - 0.55) * 1.4;
      if (wy <= 0) continue;
      for (let x = 0; x < w; x++) {
        const wx = 1 - Math.abs((x + 0.5) / w - 0.5) * 1.6;
        if (wx <= 0) continue;
        const [r, g, b] = px(x, y);
        if (Math.min(r, g, b) >= 244) continue;
        if (colouredGround && Math.abs(r - ground[0]) + Math.abs(g - ground[1]) + Math.abs(b - ground[2]) < 42) continue;
        const wt = wx * wy;
        kept.push(r, g, b, wt);
        const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
        const e = bins.get(key) || [0, 0, 0, 0];
        e[0] += wt; e[1] += r * wt; e[2] += g * wt; e[3] += b * wt;
        bins.set(key, e);
        total += wt;
      }
    }
    if (!total) return [245, 245, 245];

    /* Patches: the strongest bin swallows its neighbours one step away on every channel */
    const order = [...bins.keys()].sort((a, b) => bins.get(b)[0] - bins.get(a)[0]);
    const used = new Set();
    const patches = [];
    for (const key of order) {
      if (used.has(key)) continue;
      const kr = key >> 8, kg = (key >> 4) & 15, kb = key & 15;
      const acc = [0, 0, 0, 0];
      for (let dr = -1; dr <= 1; dr++) for (let dg = -1; dg <= 1; dg++) for (let db = -1; db <= 1; db++) {
        const r = kr + dr, g = kg + dg, b = kb + db;
        if (r < 0 || g < 0 || b < 0 || r > 15 || g > 15 || b > 15) continue;
        const n = (r << 8) | (g << 4) | b;
        if (!bins.has(n) || used.has(n)) continue;
        used.add(n);
        const e = bins.get(n);
        for (let i = 0; i < 4; i++) acc[i] += e[i];
      }
      patches.push({ share: acc[0] / total, rgb: [acc[1], acc[2], acc[3]].map((v) => Math.round(v / acc[0])) });
      if (patches.length >= 8) break;
    }

    /* The first colour the name gives is the main one ("Blue Melange" is blue, "Black/White" black) */
    const W = this.constructor.words;
    const word = (label.toLowerCase().match(/[a-z]+/g) || []).find((wd) => wd in W.hue || wd in W.neutral);

    /* A model's skin is not the product: when the strongest patch looks like skin and the name
       does not say so, a patch at least half as strong that is not skin wins */
    let pick = patches[0];
    if (W.skin(pick.rgb) && !(word && W.fits(pick.rgb, word))) {
      const other = patches.slice(1).find((p) => !W.skin(p.rgb) && p.share >= pick.share * 0.5);
      if (other) pick = other;
    }
    let rgb = pick.rgb;

    /* The name names a colour the patch is not: every counted pixel of that colour, if there
       are enough of them (pixel by pixel, so it does not hang on where the bins fall) */
    if (word && !W.fits(rgb, word)) {
      const skinOk = W.neutral[word] === 'beige' || W.neutral[word] === 'brown';
      const chroma = word in W.hue;
      const acc = [0, 0, 0, 0];
      for (let i = 0; i < kept.length; i += 4) {
        const c = [kept[i], kept[i + 1], kept[i + 2]];
        if (!W.fits(c, word) || (!skinOk && W.skin(c))) continue;
        /* a named hue: only its clear pixels, not the dark fringe around a print */
        if (chroma) { const [, l, sat] = W.hls(c); if (sat < 0.4 || l < 0.2) continue; }
        const wt = kept[i + 3];
        acc[0] += wt; acc[1] += c[0] * wt; acc[2] += c[1] * wt; acc[3] += c[2] * wt;
      }
      if (acc[0] / total >= 0.04) rgb = [acc[1], acc[2], acc[3]].map((v) => Math.round(v / acc[0]));
    }
    return rgb;
  }

  showColour(dot, row = dot && dot.closest('.card-colors')) {
    const image = row && row.closest('x-cell[data-product]')?.querySelector('.card-image');
    if (!image) return;
    const src = dot && !dot.classList.contains('is-current') ? dot.dataset.cardImg : '';
    this.colourShown = src;
    if (!src) { image.classList.remove('is-peek'); return; }
    let peek = image.querySelector('.card-peek');
    if (!peek) {
      peek = document.createElement('img');
      peek.className = 'card-peek';
      peek.alt = '';
      (image.querySelector('a') || image).append(peek);
    }
    const show = () => { if (this.colourShown === src) image.classList.add('is-peek'); };
    if (peek.getAttribute('src') !== src) {
      image.classList.remove('is-peek');
      peek.onload = show;
      peek.src = src;
    } else if (peek.complete) {
      show();
    }
  }
};
new window.CardColours();
}
